import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCalendar,
  FiEdit2,
  FiPlus,
  FiSearch,
  FiTrash2,
} from "react-icons/fi";
import SeasonModal from "../../components/masters/SeasonModal";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import ClientPagination, { useClientPagination } from "../../components/common/ClientPagination";

const formatDate = (value) => {
  if (!value) return "—";

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
};

export default function SeasonsPage() {
  const { accessToken } = useAuth();

  const [seasons, setSeasons] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingSeason, setEditingSeason] = useState(null);

  const loadSeasons = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const response = await apiRequest("/seasons", { accessToken });
      setSeasons(
        Array.isArray(response) ? response : response?.data || [],
      );
    } catch (requestError) {
      setError(requestError.message || "Unable to load seasons.");
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadSeasons();
  }, [loadSeasons]);

  const filteredSeasons = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) return seasons;

    return seasons.filter((season) =>
      [season.name, season.code].some((value) =>
        value?.toLowerCase().includes(query),
      ),
    );
  }, [search, seasons]);

  const stats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const active = seasons.filter((season) => season.isActive).length;
    const upcoming = seasons.filter(
      (season) => season.startDate?.slice(0, 10) > today,
    ).length;

    return {
      total: seasons.length,
      active,
      inactive: seasons.length - active,
      upcoming,
    };
  }, [seasons]);
  const seasonsPagination = useClientPagination(filteredSeasons);

  const handleSave = async (form) => {
    setError("");

    try {
      if (editingSeason) {
        await apiRequest(`/seasons/${editingSeason.id}`, {
          method: "PATCH",
          accessToken,
          body: form,
        });
      } else {
        await apiRequest("/seasons", {
          method: "POST",
          accessToken,
          body: {
            code: form.code,
            name: form.name,
            startDate: form.startDate,
            endDate: form.endDate,
          },
        });
      }

      setShowModal(false);
      setEditingSeason(null);
      await loadSeasons();
    } catch (requestError) {
      setError(requestError.message || "Unable to save season.");
    }
  };

  const handleDelete = async (season) => {
    if (
      !window.confirm(
        `Delete "${season.name}"? This will fail if products still use it.`,
      )
    ) {
      return;
    }

    setError("");

    try {
      await apiRequest(`/seasons/${season.id}`, {
        method: "DELETE",
        accessToken,
      });

      await loadSeasons();
    } catch (requestError) {
      setError(requestError.message || "Unable to delete season.");
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Masters / Seasons</p>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Seasons Management
          </h1>
          <p className="mt-2 text-gray-500">
            Manage fashion seasons and product collections.
          </p>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingSeason(null);
            setShowModal(true);
          }}
          className="flex h-11 items-center justify-center gap-2 rounded-xl bg-primary-800 px-5 text-white hover:bg-primary-900"
        >
          <FiPlus />
          Add Season
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="mb-8 grid gap-5 md:grid-cols-4">
        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Total Seasons</p>
          <h2 className="mt-2 text-3xl font-bold">{stats.total}</h2>
        </div>
        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Active</p>
          <h2 className="mt-2 text-3xl font-bold text-green-600">
            {stats.active}
          </h2>
        </div>
        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Upcoming</p>
          <h2 className="mt-2 text-3xl font-bold text-blue-600">
            {stats.upcoming}
          </h2>
        </div>
        <div className="rounded-3xl border bg-white p-6">
          <p className="text-sm text-gray-500">Inactive</p>
          <h2 className="mt-2 text-3xl font-bold text-red-500">
            {stats.inactive}
          </h2>
        </div>
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="border-b border-gray-100 p-5">
          <div className="relative max-w-md">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search seasons..."
              className="h-12 w-full rounded-xl border border-gray-200 pl-12 pr-4 outline-none focus:border-primary-700 focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">Season</th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">Code</th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">Duration</th>
                <th className="px-6 py-4 text-left text-xs uppercase text-gray-500">Status</th>
                <th className="px-6 py-4 text-right text-xs uppercase text-gray-500">Actions</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-sm text-gray-500">
                    Loading seasons...
                  </td>
                </tr>
              ) : filteredSeasons.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-sm text-gray-500">
                    No seasons found.
                  </td>
                </tr>
              ) : (
                seasonsPagination.pageItems.map((season) => (
                  <tr key={season.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-700">
                          <FiCalendar size={20} />
                        </div>
                        <p className="font-semibold text-gray-900">{season.name}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="rounded-lg bg-gray-100 px-2.5 py-1 font-mono text-sm text-gray-700">
                        {season.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {formatDate(season.startDate)} — {formatDate(season.endDate)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        season.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-600"
                      }`}>
                        {season.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingSeason(season);
                            setShowModal(true);
                          }}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border text-gray-600 hover:border-primary-300 hover:bg-primary-50 hover:text-primary-800"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(season)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border text-red-500 hover:border-red-200 hover:bg-red-50"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <ClientPagination pagination={seasonsPagination} label="seasons" />
      </div>

      <SeasonModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingSeason(null);
        }}
        onSave={handleSave}
        initialData={editingSeason}
      />
    </div>
  );
}
